import { GetTotalPointsProgress$Request, GoalId, TotalPointsProgress } from '../../services/api.types'
import { msgid, ngettext, t } from 'ttag'
import { formatThousands } from '../../common/helpers/translations'
import * as React from 'react'
import { Fragment } from 'react'
import './YumCustomTable.scss'
import { getPeriodString } from '../../common/helpers/date'
import { unitsDisplayPointsShort } from '../../common/helpers/units'
import dayjs from '../../dayjsConfig'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Link } from 'react-router-dom'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { isRtl } from '../../i18nInit'
import { primaryBlue } from '../../styles/material-ui-theme-v2'

export interface IYumTotalPointsTableProps {
  goalId: GoalId
  pointsProgress: TotalPointsProgress[]
}
export interface IYumTotalPointsTableDispatchProps {
  getTotalPointsProgress(payload: GetTotalPointsProgress$Request): void
}

const formatChallengePeriod = (startDate: string, endDate: string) => {
  // Convert string dates to dayjs objects
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  // Get the difference in days
  const days = end.diff(start, 'day') + 1
  const daysText = days ? ngettext(msgid`day`, `days`, days) : ''

  return getPeriodString(start.valueOf(), end.valueOf(), true) + ` (${days} ${daysText})`
}

const useStyles = makeStyles((theme) => ({
  link: {
    userSelect: 'none',
    color: primaryBlue,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:focus': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:active': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'underline !important',
      },
    },
  },
}))

export default function YumTotalPointsTable(props: IYumTotalPointsTableProps & IYumTotalPointsTableDispatchProps) {
  const classes = useStyles()
  const { goalId, pointsProgress, getTotalPointsProgress } = props
  React.useEffect(() => {
    getTotalPointsProgress({ goalId })

    let appStateChangeListener: PluginListenerHandle | undefined
    const setAppStateChangeListener = async () => {
      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        try {
          if (isActive) {
            getTotalPointsProgress({ goalId })
          }
        } catch (e: any) {
          // It's fine if it fails.
        }
      })
    }
    setAppStateChangeListener()

    return () => {
      if (appStateChangeListener) {
        try {
          appStateChangeListener.remove()
        } catch (error) {
          console.error('YumTotalPointsTable | appStateChangeListener.remove() error', error)
        }
      }
    }
  }, [])

  // TODO: refresh on app resume

  const valueBEM = (met: boolean) => {
    const base = 'data-item__value'
    const classArray = [base]
    if (met) {
      classArray.push(`${base}--met`)
    }
    return classArray.join(' ')
  }

  return (
    <table className={'custom-table custom-table__me'}>
      <thead>
        <tr>
          <th scope="col">{t`Challenge`}</th>
          <th scope="col" className={'data-item__value'}>
            {unitsDisplayPointsShort}
          </th>
        </tr>
      </thead>
      <tbody>
        {pointsProgress &&
          pointsProgress.map(({ id, title, startDate, endDate, points }, keyIndex) => {
            return (
              <Fragment key={keyIndex}>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div>
                        <div className={'data-item__challenge'}>
                          <Link className={classes.link} to={`/challenges/${id}/me`}>
                            {title}
                          </Link>
                        </div>
                        <div className={'data-item__date'}>{formatChallengePeriod(startDate, endDate)}</div>
                      </div>
                    </div>
                  </td>
                  <td className={valueBEM(points > 0)}>{formatThousands(points)}</td>
                </tr>
              </Fragment>
            )
          })}
      </tbody>
    </table>
  )
}
